import {
  Database, Wallet, ShieldCheck, BadgeCheck, FileText,
  Mail, Eye, Users, Lock, Settings, UserCog
} from 'lucide-react';

export const BRAND = 'Tangibl3Net';
export const WEB_DOMAIN = 'tangibl3.net';
export const WEB_URL = 'https://tangibl3.net';
export const WEB3_TLD = 'tangiblenet';
export const WEB3_NAMESPACE = '.tangiblenet';
export const APP_URL = '/apply';
export const SUPPORT_URL = '/support';
export const CONTACT_EMAIL = 'contact@tangibl3.net';
export const LAB_APPLICATION_EMAIL = 'onboarding@tangibl3.net';
export const SUPPORT_EMAIL_URL = 'mailto:contact@tangibl3.net';
export const CONSULT_URL = '/#package-fit';

export const navLinks = [
  { label: 'Home', href: '/#top' },
  { label: 'How It Works', href: '/#how' },
  { label: 'Tokenization Lab', href: '/#programs' },
  { label: 'Business Development', href: '/business-development' },
  { label: 'Contest', href: '/program-rules#contest' },
  { label: 'Program Rules', href: '/program-rules' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Apply', href: '/apply' },
];

export const heroStats = [
  { value: '90', label: 'Day Rule' },
  { value: '50%', label: 'Fee-Cover Cap' },
  { value: '$10k', label: 'Quarterly Contest' },
];

export const terminalLines = [
  'Opening Tangibl3Net Tokenization Lab file',
  'Checking compliance file completeness',
  'Checking responsible-party roles',
  'Preparing controlled digital certificate setup',
  'Applying compliance guardrails',
  'Creating holder records',
];

export const platformFeatures = [
  { icon: Database, title: 'Issuer File and Evidence Checks', desc: 'Collect issuer, founder, fiduciary, NGO, or asset-sponsor profiles with documents, evidence files, risk disclosures, and asset-backed records before controlled certificate setup.' },
  { icon: Wallet, title: 'Wallet Coordination', desc: 'Coordinate wallet settlement, holder records, approval states, and infrastructure planning for compliance-ready certificate programs.' },
  { icon: ShieldCheck, title: 'Restricted Transfer Controls', desc: 'Use restricted transfers, eligibility checks, whitelisting, and controlled transaction administration for compliant certificate programs.' },
  { icon: BadgeCheck, title: 'Complete-File Status', desc: 'Track whether the issuer file, asset documents, evidence records, holder records, wallet details, and transfer policy are complete.' },
  { icon: FileText, title: 'Audit and Accounting Exports', desc: 'Maintain holder records, transfer files, evidence records, and audit-ready exports for compliance, accounting, and audit support.' },
];

export const howItWorks = [
  { num: '01', icon: FileText, title: 'Assess the compliance file', desc: 'Collect issuer identity, responsible parties, jurisdiction, documents, asset evidence, and tokenization goals.' },
  { num: '02', icon: Settings, title: 'Structure the certificate program', desc: 'Route the program into donation, membership, access, secured note, warehouse/warrant, fiduciary participation, or registry workflow.' },
  { num: '03', icon: Wallet, title: 'Digitize the controlled certificate', desc: 'Prepare wallet coordination, certificate metadata, transfer policy, holder rules, and activation requirements.' },
  { num: '04', icon: Database, title: 'Settle and maintain records', desc: 'Settle through controlled arrangements and track holders, transaction records, evidence files, and accounting or audit exports.' },
  { num: '05', icon: ShieldCheck, title: 'Administer controlled requests', desc: 'Use approval workflows and controlled OTC support where permitted instead of unrestricted trading.' },
];

export const walletOptions = [
  { name: 'Issuer Workspace Wallet', tag: 'Email / Workspace', recommended: true },
  { name: 'MetaMask', tag: 'Polygon / EVM' },
  { name: 'WalletConnect', tag: 'Multi-wallet' },
];

export const deploymentChecklist = [
  'Compliance file opened before certificate configuration',
  'Documents and responsible parties identified',
  'Source-of-truth checklist generated by program type',
  'Wallet and .tangiblenet identity prepared',
  'Compliance check required before activation',
];

export const tokenFeatures = [
  { label: 'Compliance file', tag: 'Required' },
  { label: 'Transfer permissions', tag: 'Policy' },
  { label: 'Holder records', tag: 'Tracked' },
  { label: 'Evidence vault', tag: 'Linked' },
  { label: 'Compliance check', tag: 'Required' },
];

export const networks = [
  { sym: 'POL', name: 'Polygon PoS', type: 'Primary network', soon: false },
  { sym: 'AMOY', name: 'Polygon Amoy', type: 'Testnet', soon: false },
  { sym: 'EVM', name: 'EVM-compatible roadmap', type: 'Planned', soon: true },
];

export const accessControls = [
  { icon: Eye, title: 'Public summary', desc: 'Non-sensitive program overview' },
  { icon: Users, title: 'Approved parties', desc: 'Issuer, compliance reviewer, fiduciary, producer, or counterparty access' },
  { icon: UserCog, title: 'Explicit access', desc: 'Granted file by file or role by role' },
];

export const dataRoomFiles = [
  { icon: Lock, name: 'Issuer_Profile.json', size: '12 KB', tag: 'Required', tagColor: 'amber' },
  { icon: FileText, name: 'Legal_Instrument.pdf', size: '2.4 MB', tag: 'Private', tagColor: 'blue' },
  { icon: ShieldCheck, name: 'Asset_Evidence.pdf', size: '1.8 MB', tag: 'Evidence', tagColor: 'green' },
  { icon: Database, name: 'Holder_Records.csv', size: '856 KB', tag: 'Records', tagColor: 'blue' },
  { icon: Settings, name: 'Transfer_Rules.json', size: '18 KB', tag: 'Controls', tagColor: 'amber' },
  { icon: Wallet, name: 'Wallet_Attestation.json', size: '9 KB', tag: 'Wallet', tagColor: 'green' },
];

export const footerCols = [
  { title: 'Product', links: [
    { label: 'Tokenization Lab', href: '/apply' },
    { label: 'Business Development', href: '/business-development' },
    { label: 'Program Rules', href: '/program-rules' },
    { label: 'FAQ', href: '/faq' },
  ] },
  { title: 'Resources', links: [
    { label: 'Support', href: '/support' },
    { label: 'Apply', href: '/apply' },
    { label: 'Contact', href: 'mailto:contact@tangibl3.net' },
  ] },
];

export const socialLinks = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/tangibl3net/' },
  { label: 'Instagram', href: 'https://www.instagram.com/tangibl3net/' },
  { label: 'YouTube', href: 'https://www.youtube.com/@Tangibl3Net' },
];

export { CONTACT_EMAIL as contactEmail };
